import { FC, ReactNode } from "react";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PUBLIC_FORM_MAX_WIDTH, publicFormWrapper } from "../publicFormLayout";
import { publicPageCard } from "../publicPageSurfaces";
import PublicPageLogo from "./PublicPageLogo";

interface PublicPageCardProps {
    children: ReactNode;
    /** Form measure. Use WIDE_PUBLIC_FORM_MAX_WIDTH for the paired-field registration forms. */
    maxWidth?: number;
    /**
     * Rendered centred at the top of the card. Defaults to the tenant logo, falling back to the
     * 1health wordmark; pass `null` to suppress it (the result screens, which lead with a status icon).
     */
    logo?: ReactNode;
    className?: string;
}

/**
 * The centred card that holds a public page's content (NG-8606).
 *
 * Every public page had grown its own measure, padding and heading treatment. This is the one surface
 * they now share: a bordered card on the page canvas painted by GenericPublicPageWrapper, leading with
 * a centred logo and then the page's own content.
 *
 * Two theme quirks are corrected here rather than in the app theme, because the app theme is shared
 * with ~200 authenticated screens and is deliberately out of scope for NG-8606:
 *
 *  - `MuiFormHelperText` is globally `textAlign: "end"`, which puts validation messages under the
 *    right edge of the field, away from the error they describe.
 *  - `MuiOutlinedInput` pins `.Mui-focused { borderWidth: 1px !important }`, so the design system's
 *    focus ring cannot come from the notched outline — it has to be a real `outline`.
 */
const PublicPageCard: FC<PublicPageCardProps> = ({ children, maxWidth = PUBLIC_FORM_MAX_WIDTH, logo = <PublicPageLogo />, className }) => (
    <Box
        className={className}
        sx={{
            ...publicFormWrapper(maxWidth),
            paddingBlock: 4,
        }}
    >
        <Box
            // The card is a pure surface with no role or accessible name of its own, so the branding
            // e2e (which asserts it stays a different colour from the canvas) has nothing else to grab.
            data-testid="public-page-card"
            sx={(theme) => ({
                ...publicPageCard(theme),
                width: "100%",

                "& .MuiFormHelperText-root": { textAlign: "start" },

                // Focused field: a Network Teal border with a soft Signal Aqua halo, per the design
                // system. This has to go through the notched outline rather than a CSS `outline` — the
                // floating label sits ON the input's top border, so a real outline draws a line straight
                // through the label text. The `!important` is required to beat the app theme's own
                // `borderWidth: 1px !important` on `.Mui-focused` (muiUtils.ts), which is out of scope
                // to change here.
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.teal?.main,
                    borderWidth: "2px !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused": {
                    // Blur, no spread. A spread ring fills the gap the notched outline leaves for the
                    // floating label, which then reads as a line struck through the label text.
                    boxShadow: `0 0 8px ${alpha(theme.palette.action.active, 0.45)}`,
                },

                // Buttons and links carry no floating label, so they take the design system's ring.
                "& .MuiButtonBase-root:focus-visible, & a:focus-visible": {
                    outline: `2px solid ${theme.palette.action.active}`,
                    outlineOffset: 2,
                },
            })}
        >
            {logo && <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>{logo}</Box>}
            {children}
        </Box>
    </Box>
);

export default PublicPageCard;
